// src/components/AwardsPage.tsx
import React, { useEffect } from "react";
import { useLocation } from "react-router-dom";
import styles from "../styles/AwardsPage.module.css";
import profilePic from "../resources/OIP.jpg";

const AwardsPage = () => {
  const location = useLocation();

  useEffect(() => {
    // Handle scroll to section when page loads with hash
    const hash = location.hash.replace("#", "");
    if (hash) {
      const element = document.getElementById(hash);
      if (element) {
        element.scrollIntoView({ behavior: "smooth" });
      }
    } else {
      // Scroll to top by default
      window.scrollTo(0, 0);
    }
  }, [location]);

  return (
    <div className={styles.awardsContainer}>
      {/* Hero Section */}
      <div className={styles.heroSection}>
        <div className={styles.heroContent}>
          <h1>Department Honors & Achievements</h1>
          <p>
            Celebrating excellence and recognition within our academic community
          </p>
        </div>
      </div>

      {/* Awards Section */}
      <section id="awards" className={styles.section}>
        <div className={styles.sectionHeader}>
          <h2 className={styles.sectionTitle}>Department Awards</h2>
          <p className={styles.sectionIntro}>
            Our department has been recognized nationally and internationally
            for excellence in philosophical research and education.
          </p>
        </div>

        <div className={styles.awardsGrid}>
          {/* Award Card 1 */}
          <div className={styles.awardCard}>
            <div className={styles.awardIcon}>🏅</div>
            <h3>National Philosophy Award</h3>
            <div className={styles.awardMeta}>
              <span>2023</span>
              <span>Academic Excellence</span>
            </div>
            <p>
              Recognized as the top philosophy department in the nation for
              research output and student satisfaction.
            </p>
          </div>

          {/* Award Card 2 */}
          <div className={styles.awardCard}>
            <div className={styles.awardIcon}>🏆</div>
            <h3>International Ethics Prize</h3>
            <div className={styles.awardMeta}>
              <span>2022</span>
              <span>Applied Ethics</span>
            </div>
            <p>
              Awarded for our groundbreaking research in bioethics and its
              real-world applications in healthcare policy.
            </p>
          </div>

          {/* Award Card 3 */}
          <div className={styles.awardCard}>
            <div className={styles.awardIcon}>✨</div>
            <h3>Center of Excellence</h3>
            <div className={styles.awardMeta}>
              <span>2021</span>
              <span>Research Innovation</span>
            </div>
            <p>
              Designated as a Center of Excellence in Philosophy of Mind and
              Cognitive Science by the International Philosophy Association.
            </p>
          </div>

          {/* Award Card 4 */}
          <div className={styles.awardCard}>
            <div className={styles.awardIcon}>📜</div>
            <h3>Best Publication Award</h3>
            <div className={styles.awardMeta}>
              <span>2020</span>
              <span>Academic Publishing</span>
            </div>
            <p>
              Recognized for "Ethics in the Digital Age," the most cited
              philosophy publication of the year.
            </p>
          </div>
        </div>
      </section>

      {/* Notable Appointments Section */}
      <section id="appointments" className={styles.section}>
        <div className={styles.sectionHeader}>
          <h2 className={styles.sectionTitle}>Notable Appointments</h2>
          <p className={styles.sectionIntro}>
            Our faculty members have been recognized with prestigious
            appointments and positions of influence.
          </p>
        </div>

        <div className={styles.appointmentsGrid}>
          {/* Appointment 1 */}
          <div className={styles.appointmentCard}>
            <div
              className={styles.profileImage}
              style={{ backgroundImage: `url(${profilePic})` }}
            ></div>
            <div className={styles.profileInfo}>
              <h3>Prof. Adaobi Nnamdi</h3>
              <p className={styles.position}>Chair of Philosophy Department</p>
              <div className={styles.appointmentMeta}>
                <span>Governor's Ethics Advisor</span>
                <span>2023-Present</span>
              </div>
              <p>
                Appointed by the Governor to advise on ethical policy decisions
                and public integrity matters.
              </p>
            </div>
          </div>

          {/* Appointment 2 */}
          <div className={styles.appointmentCard}>
            <div
              className={styles.profileImage}
              style={{ backgroundImage: `url(${profilePic})` }}
            ></div>
            <div className={styles.profileInfo}>
              <h3>Dr. Chike Anya</h3>
              <p className={styles.position}>Associate Professor</p>
              <div className={styles.appointmentMeta}>
                <span>Presidential Science Advisor</span>
                <span>2022-Present</span>
              </div>
              <p>
                Serving on the Presidential Council of Science Advisors,
                focusing on AI ethics and policy.
              </p>
            </div>
          </div>

          {/* Appointment 3 */}
          <div className={styles.appointmentCard}>
            <div
              className={styles.profileImage}
              style={{ backgroundImage: `url(${profilePic})` }}
            ></div>
            <div className={styles.profileInfo}>
              <h3>Dr. Fatima Aliyu</h3>
              <p className={styles.position}>Assistant Professor</p>
              <div className={styles.appointmentMeta}>
                <span>UN Human Rights Council</span>
                <span>2021-Present</span>
              </div>
              <p>
                Appointed to the UN Human Rights Council working group on gender
                equality and social justice.
              </p>
            </div>
          </div>

          {/* Appointment 4 */}
          <div className={styles.appointmentCard}>
            <div
              className={styles.profileImage}
              style={{ backgroundImage: `url(${profilePic})` }}
            ></div>
            <div className={styles.profileInfo}>
              <h3>Dr. Emeka Obi</h3>
              <p className={styles.position}>Senior Lecturer</p>
              <div className={styles.appointmentMeta}>
                <span>National Science Foundation</span>
                <span>2020-Present</span>
              </div>
              <p>
                Chairing the NSF committee on ethical guidelines for emerging
                technologies research.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Alumni Achievements */}
      <section id="alumni" className={styles.section}>
        <div className={styles.sectionHeader}>
          <h2 className={styles.sectionTitle}>Distinguished Alumni</h2>
          <p className={styles.sectionIntro}>
            Our graduates have gone on to achieve remarkable success in various
            fields.
          </p>
        </div>

        <div className={styles.alumniGrid}>
          {/* Alumni 1 */}
          <div className={styles.alumniCard}>
            <div
              className={styles.profileImage}
              style={{ backgroundImage: `url(${profilePic})` }}
            ></div>
            <div className={styles.alumniInfo}>
              <h3>Senator James Okoro</h3>
              <p>BA Philosophy '05</p>
              <p className={styles.achievement}>
                State Senator, Chair of Ethics Committee
              </p>
            </div>
          </div>

          {/* Alumni 2 */}
          <div className={styles.alumniCard}>
            <div
              className={styles.profileImage}
              style={{ backgroundImage: `url(${profilePic})` }}
            ></div>
            <div className={styles.alumniInfo}>
              <h3>Dr. Amina Yusuf</h3>
              <p>PhD Philosophy '12</p>
              <p className={styles.achievement}>
                Ambassador to the United Nations
              </p>
            </div>
          </div>

          {/* Alumni 3 */}
          <div className={styles.alumniCard}>
            <div
              className={styles.profileImage}
              style={{ backgroundImage: `url(${profilePic})` }}
            ></div>
            <div className={styles.alumniInfo}>
              <h3>Sarah Johnson</h3>
              <p>MA Ethics & Policy '15</p>
              <p className={styles.achievement}>Supreme Court Justice</p>
            </div>
          </div>
            {/* Alumni 4 */}
           <div className={styles.alumniCard}>
            <div
              className={styles.profileImage}
              style={{ backgroundImage: `url(${profilePic})` }}
            ></div>
            <div className={styles.alumniInfo}>
              <h3>Sarah Johnson</h3>
              <p>MA Ethics & Policy '15</p>
              <p className={styles.achievement}>Supreme Court Justice</p>
            </div>
          </div>
            {/* Alumni 5 */}
           <div className={styles.alumniCard}>
            <div
              className={styles.profileImage}
              style={{ backgroundImage: `url(${profilePic})` }}
            ></div>
            <div className={styles.alumniInfo}>
              <h3>Sarah Johnson</h3>
              <p>MA Ethics & Policy '15</p>
              <p className={styles.achievement}>Supreme Court Justice</p>
            </div>
          </div>

          {/* Alumni 6 */}
          <div className={styles.alumniCard}>
            <div
              className={styles.profileImage}
              style={{ backgroundImage: `url(${profilePic})` }}
            ></div>
            <div className={styles.alumniInfo}>
              <h3>Dr. Kwame Mensah</h3>
              <p>PhD Philosophy '08</p>
              <p className={styles.achievement}>Minister of Education</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <div className={styles.ctaSection}>
        <h2>Join Our Legacy of Excellence</h2>
        <p>
          Become part of a department that cultivates leaders and innovators
        </p>
        <div className={styles.ctaButtons}>
          <button className={styles.primaryBtn}>Apply Now</button>
          <button className={styles.secondaryBtn}>
            Learn About Our Programs
          </button>
        </div>
      </div>
    </div>
  );
};

export default AwardsPage;
